import React, { forwardRef, useContext } from 'react';
import classNames from 'classnames';
import { useMergeRefs, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';

import { AppContainerContext, Comp, useTheme } from '@redsift/design-system';
import { PopoverContentProps } from './types';
import { usePopoverContext } from '../popover';
import { StyledPopoverContent } from './styles';

const COMPONENT_NAME = 'PopoverContent';
const CLASSNAME = 'redsift-popover-content';

/**
 * The PopoverContent component.
 */
export const PopoverContent: Comp<PopoverContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, style, theme: propsTheme, ...forwardedProps } = props;
  const appContainerState = useContext(AppContainerContext);
  const {
    context: floatingContext,
    getFloatingProps,
    isModal,
    isOpen,
    refs,
    strategy,
    x,
    y,
    hideInsteadOfClose,
  } = usePopoverContext();
  const theme = useTheme(propsTheme);
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const RenderedPopoverContent = (
    <StyledPopoverContent
      $theme={theme!}
      {...getFloatingProps(props)}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        width: 'fit-content',
        display: hideInsteadOfClose && !isOpen ? 'none' : 'flex',
        ...style,
      }}
      className={classNames(PopoverContent.className, className)}
      {...forwardedProps}
      ref={popoverRef}
    >
      {children}
    </StyledPopoverContent>
  );

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen || hideInsteadOfClose ? (
        <FloatingFocusManager context={floatingContext} modal={isModal}>
          {RenderedPopoverContent}
        </FloatingFocusManager>
      ) : null}
    </FloatingPortal>
  );
});
PopoverContent.className = CLASSNAME;
PopoverContent.displayName = COMPONENT_NAME;
